import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, Outlet, Route, useLocation, useMatch, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Badge, Button, buttonClass, Card, cx, DialogShell, IconArrowLeft, IconChevronLeft, IconChevronRight, IconMore, IconPhone,
    LoadingList, MenuButton,
} from '@timely/ui';
import { DEFAULT_SORT, RANK_WORDS, filtersFromParams, firstDialable, type BookSort } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page, useScreenChrome } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { setUrgent, useCustomerDetail, useNeighbours, type Neighbours } from '../book/useBook';
import { paths, type AccountTab } from '../workspace/navigation';
import { SendReminder } from '../templates/SendReminder';
import { ChequeDialog } from '../cheques/ChequeDialog';
import { accountCode } from '../book/accountCode';
import { AccountProvider, mayContact, useAccount, useAccountPage, type AccountDetailData } from './customer/accountPage';
import { MoneyBand, MoneyStrip } from './customer/MoneyBand';
import { AccountTabs } from './customer/AccountTabs';
import { ActivityRail } from './customer/Timeline';
import { RecordOutcomeComposer, RecordOutcomeDialog } from './customer/RecordOutcome';
import { CustomerOverview } from './customer/Overview';
import { CustomerActivity } from './customer/Activity';
import { CustomerBills } from './customer/Bills';
import { CustomerContacts } from './customer/Contacts';
import { CustomerLedger } from './customer/Ledger';

/**
 * The account's addresses (Customer 360, decision 6): Overview at the account's own address, and each other tab at
 * its own. The Ledger's address opens Overview where there is no Ledger to read (0038). Anything else under the
 * account — an old link, a typo — opens Overview.
 */
export const accountRoutes = (path = 'customers/:customerId') => (
    <Route path={path} element={<CustomerDetail />}>
        <Route index element={<CustomerOverview />} />
        <Route path="activity" element={<CustomerActivity />} />
        <Route path="bills" element={<CustomerBills />} />
        <Route path="ledger" element={<CustomerLedger />} />
        <Route path="contacts" element={<CustomerContacts />} />
        <Route path="*" element={<ToOverview />} />
    </Route>
);

const TABS: readonly AccountTab[] = ['activity', 'bills', 'ledger', 'contacts'];

function ToOverview() {
    const { workspace } = useActiveWorkspace();
    const { customerId = '' } = useParams();
    const { search } = useLocation();
    return <Navigate to={`${paths(workspace.slug).customer(customerId)}${search}`} replace />;
}

interface Back { to: string; label: string; short: string }

/**
 * One account: where it stands, what was said, and what to do next.
 *
 * Reached by its id, and the id confers nothing. `customers_read` applies
 * `can_see_customer()`, so an account in another company, an account outside
 * this person's scope, and an account that does not exist all come back the
 * same way — and this screen says the same thing for all three. Distinguishing
 * them would confirm which ids are real.
 */
export function CustomerDetail() {
    const context = useActiveWorkspace();
    const { customerId = '' } = useParams();
    const [params] = useSearchParams();
    const { result, loading, reload } = useCustomerDetail(context, customerId);
    // Below 1024px the account takes the frame's two bars: its way back at the top, its own actions at the bottom.
    // The bottom only for somebody who may work it: a Viewer has no action there, and keeps the app's own bar.
    const chrome = useScreenChrome({ bottom: mayContact(context) });

    // Previous and next come from the same filtered, authorised list the person
    // arrived from — never from "all customers", which would be a way to walk
    // into an account they may not open.
    const neighbours = useNeighbours(
        context.workspace.id,
        customerId ?? '',
        { filters: filtersFromParams(params), sort: (params.get('sort') as BookSort) ?? DEFAULT_SORT },
    );

    // Back to where the person came from, as it was: Today's queue, or the list
    // with its filters. The queue's own markers are not list filters.
    const to = paths(context.workspace.slug);
    const fromToday = params.get('from') === 'today';
    const listParams = new URLSearchParams(params);
    listParams.delete('from');
    listParams.delete('group');
    const todayParams = new URLSearchParams();
    if (params.get('group')) todayParams.set('group', params.get('group')!);
    if (params.get('owner') === 'mine') todayParams.set('scope', 'mine');
    const back: Back = fromToday
        ? { to: `${to.today}${todayParams.toString() ? `?${todayParams}` : ''}`, label: 'Back to Today', short: 'Today' }
        : { to: `${to.customers}${listParams.toString() ? `?${listParams}` : ''}`, label: 'Back to customers', short: 'Customers' };

    const topBack = chrome.top && createPortal(
        <Link to={back.to} aria-label={back.label} className="inline-flex items-center gap-0.5 h-11 -ml-1.5 pr-2 text-body font-semibold text-chrome-label">
            <IconChevronLeft size={18} />{back.short}
        </Link>,
        chrome.top,
    );

    // A reload after a change keeps this account on the page, so what it says after the change (and where the person
    // was) is not lost to the loading list; another account, or a first load, shows the loading list (26 Oct 2026).
    const reloadingThis = result?.status === 'ok' && result.detail.customer.id === customerId;
    if (!result || (loading && !reloadingThis)) {
        return (
            <Page>
                {topBack}
                <Card className="overflow-hidden"><LoadingList label="Loading the account" rows={6} /></Card>
            </Page>
        );
    }

    if (result.status === 'not-available') {
        return (
            <Page>
                {topBack}
                <Card className="px-7 py-10 text-center" role="alert">
                    <h2 className="text-title font-semibold text-label tracking-heading leading-tight">
                        That account is not available
                    </h2>
                    <p className="text-body text-label-2 mt-3 leading-relaxed max-w-md mx-auto">
                        It may belong to another company, it may not be one of yours to work, or it
                        may no longer exist. If you think you should be able to see it, ask whoever
                        assigns accounts here.
                    </p>
                    <div className="mt-7">
                        <Link to={back.to} className="text-accent-text font-semibold hover:underline">
                            {back.label}
                        </Link>
                    </div>
                </Card>
            </Page>
        );
    }

    if (result.status === 'error') {
        return (
            <Page>
                {topBack}
                <FormNotice tone="error">{result.message}</FormNotice>
                <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
            </Page>
        );
    }

    return (
        <>
            {topBack}
            <Account
                key={customerId}
                detail={result.detail}
                back={back}
                search={params.toString()}
                neighbours={neighbours}
                bar={chrome.bottom}
                onChanged={reload}
            />
        </>
    );
}

/* -------------------------------------------------------------------------- */

/**
 * The account's page (Customer 360, decision 1; the owner's polish of 24 Oct 2026): one toolbar — who it is on the
 * left, the walk and the actions on the right, Record outcome the one strong button — then, from 1280px, the work on
 * the left and the account's activity in a rail on the right, beside Overview, Bills and Contacts. Record outcome
 * opens in that rail; the full activity takes the whole workspace. Narrower, one column, and below 1024px the
 * account's own bar (Call · Record outcome · Remind) replaces the app's for whoever may work the account. Without the
 * rail the summary card is Overview's: the other tabs open with the card in short, then the tab (owner, 24 Oct 2026).
 * A tab opens at its top, as every address does: the frame moves focus to the page's heading when the address changes.
 */
function Account({
    detail, back, search, neighbours, bar, onChanged,
}: {
    detail: AccountDetailData;
    back: Back;
    /** The list's own state, carried into the previous/next links. */
    search: string;
    /** Where this account sits in the list somebody arrived from, if it does. */
    neighbours: Neighbours | null;
    /** The frame's bottom bar, below 1024px, for the account's own actions. */
    bar: HTMLElement | null;
    onChanged: () => Promise<void>;
}) {
    const context = useActiveWorkspace();
    const match = useMatch('/w/:slug/customers/:customerId/:tab');
    const tab: AccountTab = TABS.find(t => t === match?.params.tab) ?? 'overview';
    const full = tab === 'activity';
    const [dialog, setDialog] = useState<'record' | 'remind' | 'cheque' | null>(null);
    const page = useAccountPage(detail, onChanged, {
        openRecord: () => setDialog('record'),
        openRemind: () => setDialog('remind'),
        openCheque: () => setDialog('cheque'),
    });
    const { customer, row, rights } = page;
    const walkTo = (id: string) => `${paths(context.workspace.slug).customer(id)}${search ? `?${search}` : ''}`;
    const meta = [accountCode(customer.sourceKey), customer.city, customer.category].filter(Boolean).join(' · ');
    const header = useRef<HTMLElement>(null);

    // Record outcome writes into the account's activity, so it opens there: in the rail, from 1280px — on the full
    // activity too, where the rail comes in beside the history for it. Narrower, over the page. Whether the rail has
    // room is what the stylesheet says (a probe it shows from 1280px), never a media query in script.
    const composing = page.record.composing && rights.record;
    const rail = !full || composing;
    const record = () => {
        const probe = document.getElementById('account-rail-probe');
        if (probe && probe.offsetParent !== null) { page.record.compose(true); return; }
        setDialog('record');
    };

    const toggleUrgent = () => {
        page.setBusy(true);
        page.clearNotice();
        void setUrgent(customer.id, context.workspace.id, !customer.isUrgent)
            .then(() => page.changed(customer.isUrgent ? 'No longer marked urgent.' : 'Marked urgent.'))
            .catch((error: Error) => page.failed(error.message))
            .finally(() => page.setBusy(false));
    };
    const more = [
        ...(rights.edit ? [{ label: customer.isUrgent ? 'Remove the urgent mark' : 'Mark urgent', onSelect: toggleUrgent, disabled: page.busy }] : []),
        ...(rights.recordCheque ? [{ label: 'Record a cheque', onSelect: page.openCheque, disabled: page.busy }] : []),
    ];
    const marks = (
        <>
            {customer.isUrgent && <Badge tone="dang">Urgent</Badge>}
            {row.rank === 'Bad' && <Badge tone="dang">{RANK_WORDS.Bad}</Badge>}
            {row.rank === 'Late' && <Badge tone="warn">{RANK_WORDS.Late}</Badge>}
        </>
    );

    return (
        <AccountProvider value={page}>
            <Page width="wide">
                <StickyToolbar header={header}>
                    <div className="min-w-0 flex items-center gap-2.5">
                        <span className="font-display stretch-title text-heading font-semibold text-label tracking-heading truncate">{customer.company}</span>
                        <span className="flex items-center gap-1.5 flex-none">{marks}</span>
                    </div>
                    <Actions compact more={more} onRecord={record} />
                </StickyToolbar>

                {/* Who this is, on the left; the walk and the actions on the right. No surface around it: it is the
                    page's heading, not a thing on the page. Below 1024px the way back is in the top bar and the actions
                    in the account's own bar, so only the walk stays here, above the name. */}
                <header ref={header} className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
                    <div className="min-w-0 flex-1 basis-80">
                        <Link to={back.to} className="max-lg:hidden inline-flex items-center gap-1.5 text-secondary font-semibold text-accent-text hover:underline">
                            <IconArrowLeft size={14} />{back.label}
                        </Link>
                        {/* In full on Overview; on the other tabs without the rail, two lines at most, so a long name never
                            pushes the tab off a phone's first screen (it is read in full all the same). */}
                        <h2 title={tab === 'overview' ? undefined : customer.company}
                            className={cx('lg:mt-2.5 font-display stretch-title text-title font-semibold text-label tracking-title leading-tight break-words', tab !== 'overview' && 'max-xl:line-clamp-2')}>
                            {customer.company}
                        </h2>
                        {(meta || customer.isUrgent || row.rank !== 'Good') && (
                            <div className="mt-1.5 flex items-center gap-x-2 gap-y-1 flex-wrap text-secondary text-label-3">
                                {meta && <span className="break-words">{meta}</span>}
                                {marks}
                            </div>
                        )}
                    </div>
                    <div className={cx('flex items-center gap-3 flex-wrap max-lg:order-first lg:justify-end', neighbours ? 'max-lg:w-full' : 'max-lg:hidden')}>
                        {neighbours && <Walk neighbours={neighbours} slug={context.workspace.slug} search={search} />}
                        {neighbours && <span aria-hidden="true" className="max-lg:hidden h-6 w-px bg-separator" />}
                        <div className="max-lg:hidden"><Actions compact={false} more={more} onRecord={record} /></div>
                    </div>
                </header>

                {page.notice && (
                    <div className="mt-4">
                        <FormNotice tone={page.notice.tone}>
                            {page.notice.text}
                            {page.notice.tone === 'ok' && neighbours?.next && (
                                <> <Link to={walkTo(neighbours.next.id)} className="font-semibold underline underline-offset-2">Next account: {neighbours.next.company} →</Link></>
                            )}
                        </FormNotice>
                    </div>
                )}

                <span id="account-rail-probe" aria-hidden="true" className="hidden xl:block h-0" />
                <div className={cx('mt-6 grid gap-6 items-start', rail && 'xl:grid-cols-[minmax(0,1fr)_clamp(340px,calc(32vw-80px),420px)]')}>
                    <div className="min-w-0 space-y-6">
                        {tab === 'overview' ? <MoneyBand /> : (
                            <>
                                <MoneyBand className="max-xl:hidden" />
                                <MoneyStrip className="xl:hidden" />
                            </>
                        )}
                        <div>
                            <AccountTabs current={tab} />
                            <div className="pt-5"><Outlet /></div>
                        </div>
                    </div>
                    {/* The account's activity, from 1280px: what happened lately, or Record outcome in its place. Beside
                        the full activity only for Record outcome: the recent part would repeat it. */}
                    {rail && (
                        <aside id="account-rail" aria-label={composing ? 'Record outcome' : 'Recent activity'}
                            className="hidden xl:block xl:sticky xl:top-20 xl:max-h-[calc(100dvh-6rem)] xl:overflow-y-auto min-w-0 rounded-card">
                            {composing ? <RecordOutcomeComposer /> : <ActivityRail onContinue={() => page.record.compose(true)} />}
                        </aside>
                    )}
                </div>
            </Page>

            {bar && rights.contact && createPortal(<AccountBar />, bar)}

            {dialog === 'record' && <RecordOutcomeDialog onClose={() => setDialog(null)} />}
            {dialog === 'remind' && (
                <DialogShell title="Send a reminder" subtitle={customer.company} size="md" onClose={() => setDialog(null)}>
                    <SendReminder context={context} customer={customer} bare />
                </DialogShell>
            )}
            {dialog === 'cheque' && (
                <ChequeDialog
                    cheque={null}
                    customer={{ id: customer.id, name: customer.company }}
                    onClose={() => setDialog(null)}
                    onSaved={async message => {
                        setDialog(null);
                        await page.changed(message);
                    }}
                />
            )}
        </AccountProvider>
    );
}

/**
 * The account's actions, strongest last: Call (the account's first number), Remind, and Record outcome — the one
 * solid button — then More. Somebody who may only read gets none, and is told so. `compact` is the sticky
 * toolbar's: the same actions, smaller.
 */
function Actions({ compact, more, onRecord }: {
    compact: boolean;
    more: { label: string; onSelect: () => void; disabled?: boolean }[];
    onRecord: () => void;
}) {
    const page = useAccount();
    const { customer, rights } = page;
    const contact = firstDialable(customer);
    const size = compact ? 'sm' : 'md';
    if (!rights.contact && !rights.record && more.length === 0) {
        return <span className="inline-flex items-center h-8 px-2.5 rounded-control bg-card-3 text-caption font-semibold text-label-2">Read only</span>;
    }
    return (
        <div className="flex items-center gap-2">
            {contact && rights.contact && (
                <a href={`tel:${contact.dial}`} title={`${contact.name ? `${contact.name}, ` : ''}${contact.number}`}
                    className={buttonClass({ variant: 'secondary', size, className: 'max-w-[15rem]' })}>
                    <IconPhone size={14} /><span className="truncate">{contact.name && !compact ? `Call ${contact.name}` : 'Call'}</span>
                </a>
            )}
            {rights.record && (
                <>
                    <Button size={size} variant="quiet" onClick={page.openRemind}>Remind</Button>
                    <Button size={size} variant="primary" onClick={onRecord} data-record-outcome="">Record outcome</Button>
                </>
            )}
            {more.length > 0 && (
                <MenuButton label={<IconMore size={18} />} name="More actions" variant="quiet" size={size} items={more}
                    buttonClassName={compact ? '!w-8 !px-0' : '!w-9 !px-0'} />
            )}
        </div>
    );
}

/**
 * The toolbar again, compact, once the page's own has scrolled away (from 1024px): the account's name and its
 * actions, so Record outcome is one press away on a long statement. It sits over the page's top edge rather than in
 * the flow, so the page never jumps when it comes and goes; whether it shows is the page's scroll position, read on
 * scrolling, never a guess from the window's size.
 */
function StickyToolbar({ header, children }: { header: React.RefObject<HTMLElement | null>; children: React.ReactNode }) {
    const [stuck, setStuck] = useState(false);
    useEffect(() => {
        let frame = 0;
        const check = () => { frame = 0; const el = header.current; setStuck(!!el && el.getBoundingClientRect().bottom < 0); };
        const soon = () => { if (!frame) frame = requestAnimationFrame(check); };
        window.addEventListener('scroll', soon, { passive: true });
        window.addEventListener('resize', soon);
        check();
        return () => { window.removeEventListener('scroll', soon); window.removeEventListener('resize', soon); if (frame) cancelAnimationFrame(frame); };
    }, [header]);
    return (
        <div className="max-lg:hidden sticky top-0 z-20 h-0">
            {stuck && (
                <div className="absolute inset-x-0 -mx-8 top-0 px-8 h-14 flex items-center justify-between gap-6 bg-bg/90 backdrop-blur-md border-b border-separator shadow-e1 animate-fade">
                    {children}
                </div>
            )}
        </div>
    );
}

/**
 * The account's own bar below 1024px, where the app's was (Customer 360, 02 §5.7): Call · Record outcome · Remind,
 * on every tab, for whoever may work the account (`mayContact`); somebody who may only read it keeps the app's bar.
 * Call rings the first number in the account's own order; with none, it says so rather than vanishing.
 */
function AccountBar() {
    const page = useAccount();
    const id = useId();
    const contact = firstDialable(page.customer);
    const item = 'min-w-0 h-11 inline-flex items-center justify-center gap-1.5 px-2 rounded-control text-secondary font-semibold whitespace-nowrap';
    const quiet = 'bg-chrome-2 text-chrome-label border border-chrome-line';
    return (
        <div role="group" aria-label="Work this account" className="fixed inset-x-0 bottom-0 z-30 bg-chrome border-t border-chrome-line pb-[env(safe-area-inset-bottom)]">
            <div className="grid grid-cols-[1fr_1.6fr_1fr] gap-2 px-3 h-16 items-center">
                {contact ? (
                    <a href={`tel:${contact.dial}`} aria-label={contact.name ? `Call ${contact.name}` : 'Call'} className={cx(item, quiet)}>
                        <IconPhone size={16} />Call
                    </a>
                ) : (
                    <button type="button" disabled aria-describedby={`${id}-no-number`} className={cx(item, quiet, 'opacity-55 cursor-not-allowed')}>
                        <IconPhone size={16} />Call
                    </button>
                )}
                <button type="button" onClick={page.openRecord} className={cx(item, 'bg-mark-on-chrome text-chrome')}>Record outcome</button>
                <button type="button" onClick={page.openRemind} className={cx(item, quiet)}>Remind</button>
            </div>
            {!contact && <span id={`${id}-no-number`} className="sr-only">No phone number on this account</span>}
        </div>
    );
}

/**
 * Working down a list without going back to it.
 *
 * The efficient loop is: open the first account, record what happened, move to
 * the next. Returning to the list between every customer is a press and a scroll
 * position to lose, forty times a morning.
 *
 * The sequence is the person's own filtered list, so "next" can only ever be an
 * account they were already looking at.
 */
function Walk({
    neighbours, slug, search,
}: {
    neighbours: Neighbours;
    slug: string;
    search: string;
}) {
    const to = (id: string) => `${paths(slug).customer(id)}${search ? `?${search}` : ''}`;
    const suffix = search ? ` (${neighbours.position} of ${neighbours.total})` : '';
    const navigate = useNavigate();

    // Alt + ← / → walk the list, as the buttons do — not while typing, and
    // not when a dialog is open over the page.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            const t = e.target as HTMLElement | null;
            if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
            if (document.querySelector('[aria-modal="true"]')) return;
            const target = e.key === 'ArrowLeft' ? neighbours.previous : neighbours.next;
            if (!target) return;
            e.preventDefault();
            navigate(to(target.id));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [neighbours, search, slug]);

    // From 1024px, beside the actions: the position and two arrows (their names are said in full to the ear and in
    // the tooltip). On a phone, a row of its own with the words.
    const off = buttonClass({ variant: 'secondary', size: 'sm', className: 'opacity-50 cursor-not-allowed lg:!w-8 lg:!px-0' });
    const on = buttonClass({ variant: 'secondary', size: 'sm', className: 'lg:!w-8 lg:!px-0' });

    // The position on the left and the two buttons together on the right (a tablet); a phone has room for the buttons
    // alone, one at each end; from 1024px, all three side by side.
    return (
        <div className="flex items-center gap-1.5 max-lg:w-full max-lg:justify-between">
            <span className="text-secondary text-label-3 num hidden sm:inline mr-1.5">
                {neighbours.position} of {neighbours.total}
            </span>
            <span className="flex items-center gap-1.5 max-sm:w-full max-sm:justify-between">
                {neighbours.previous ? (
                    <Link to={to(neighbours.previous.id)} aria-label={`Previous account: ${neighbours.previous.company}${suffix}`}
                        aria-keyshortcuts="Alt+ArrowLeft" title="Previous account (Alt + ←)" className={on}>
                        <IconChevronLeft /><span className="lg:hidden">Previous</span>
                    </Link>
                ) : (
                    <span aria-disabled="true" className={off}><IconChevronLeft /><span className="lg:hidden">Previous</span></span>
                )}
                {neighbours.next ? (
                    <Link to={to(neighbours.next.id)} aria-label={`Next account: ${neighbours.next.company}${suffix}`}
                        aria-keyshortcuts="Alt+ArrowRight" title="Next account (Alt + →)" className={on}>
                        <span className="lg:hidden">Next</span><IconChevronRight />
                    </Link>
                ) : (
                    <span aria-disabled="true" className={off}><span className="lg:hidden">Next</span><IconChevronRight /></span>
                )}
            </span>
        </div>
    );
}
